import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSyncAlt, FaSearch, FaUser, FaEdit, FaCheckCircle, FaExclamationCircle, FaFileCsv } from 'react-icons/fa';
import { translateMedicineName } from '../../utils/medTranslations';

interface PricePlan {
    id: number;
    plan_type: string;
    duration_months: number;
    price_tax_included: number;
    quantity_units?: number | null;
}

interface Medicine {
    id: number;
    name: string;
    service_type?: string;
    price_plans: PricePlan[];
}

interface MedicineLimitData {
    max_quantity?: number;
    used_quantity?: number;
    max_months?: number;
    used_months?: number;
    remaining_quantity?: number;
    remaining_months?: number;
}

interface Reservation {
    id: number;
    user_email: string | null;
    user_name?: string | null;
    service: string;
    date: string;
    time: string;
    status: string;
    payment_status?: string | null;
    total_amount_jpy?: number | null;
    refill_max_months: number;
    refill_used_months: number;
    refill_max_quantity: number;
    refill_used_quantity: number;
    is_refill: boolean;
    parent_reservation_id?: number | null;
    shipping_status?: string | null;
    tracking_number?: string | null;
    shipping_carrier?: string | null;
    selected_price_plan_ids?: number[] | null;
    medicine_ids?: number[] | null;
    refill_limits?: Record<string, MedicineLimitData> | null;
    user_profile?: {
        full_name?: string | null;
        chart_number?: string | null;
    };
}

const AdminAdditionalSupply: React.FC = () => {
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [medicines, setMedicines] = useState<Medicine[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [editTarget, setEditTarget] = useState<Reservation | null>(null);
    // const [editValues, setEditValues] = useState({ quantity: 0 }); // Legacy
    const [editLimits, setEditLimits] = useState<Record<number, { max_quantity: number; max_months: number }>>({});

    const [showBatchModal, setShowBatchModal] = useState(false);
    const [batchValues, setBatchValues] = useState({ quantity: 0, months: 0 });

    const [showTrackingModal, setShowTrackingModal] = useState(false);
    const [trackingTarget, setTrackingTarget] = useState<Reservation | null>(null);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [trackingCarrier, setTrackingCarrier] = useState('yamato');

    // CSV Export Logic
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvCoolType, setCsvCoolType] = useState<number>(0); // 0:Normal, 1:Cool(Fridge), 2:Cool(Frozen)
    const [csvShipDate, setCsvShipDate] = useState<string>(''); // YYYY-MM-DD
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [resReservations, resMedicines] = await Promise.all([
                api.get('/admin/reservations', { params: { is_refill: true } }),
                api.get('/admin/medicines', { params: { limit: 1000 } })
            ]);

            // No client-side filtering needed now
            setReservations(resReservations.data);
            setMedicines(resMedicines.data);
        } catch (err) {
            console.error('Error fetching refill data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const role = localStorage.getItem('role');
        if (role !== 'admin') {
            navigate('/');
            return;
        }
        fetchInitialData();
    }, [navigate]);

    // Lookup table for medicines/plans
    const planMap = useMemo(() => {
        const map = new Map<number, { medName: string, quantity: number, serviceType: string }>();
        medicines.forEach(m => {
            m.price_plans.forEach(p => {
                map.set(p.id, {
                    medName: m.name,
                    quantity: p.quantity_units || 0,
                    serviceType: m.service_type || ''
                });
            });
        });
        return map;
    }, [medicines]);

    const getMedicineDisplay = (r: Reservation) => {
        const plans = r.selected_price_plan_ids || [];
        let planMonths = (r as any).selected_plan_months || (r as any).selected_price_plan_months || {};
        if (typeof planMonths === 'string') {
            try {
                planMonths = JSON.parse(planMonths);
            } catch (e) {
                console.error("Failed to parse planMonths", e);
                planMonths = {};
            }
        }

        if (plans.length > 0) {
            const names = plans.map(pid => {
                const info = planMap.get(pid);
                if (!info || info.serviceType === 'shipping') return null;
                const unitBase = info.quantity || 1;
                // Handle various formats of planMonths (keys can be string or number)
                let rawMonths = planMonths[pid] || planMonths[String(pid)] || 1;

                let months = Number(rawMonths);

                // Safeguard against NaN
                if (isNaN(months) || months < 1) months = 1;

                const totalQuantity = unitBase * months;
                const translatedName = translateMedicineName(info.medName, i18n.language);

                return `${translatedName} (${totalQuantity}${t('admin.quantity_unit', '個')})`;
            }).filter(Boolean);

            if (names.length > 0) return names.join(', ');
        }

        // Fallback: Use medicine_ids for old reservations
        const medIds = r.medicine_ids || [];
        if (medIds.length > 0) {
            const medNames = medIds.map((mid: number) => {
                const med = medicines.find(m => m.id === mid);
                if (med && med.service_type !== 'shipping') {
                    const translatedName = translateMedicineName(med.name, i18n.language);
                    return `${translatedName} (1${t('admin.quantity_unit', '个')})`;
                }
                return null;
            }).filter(Boolean);
            if (medNames.length > 0) return medNames.join(', ');
        }

        return '-';
    };

    const getShippingDisplay = (r: Reservation) => {
        const plans = r.selected_price_plan_ids || [];
        return plans.map((pid: number) => {
            const info = planMap.get(pid);
            if (info && info.serviceType === 'shipping') {
                return translateMedicineName(info.medName, i18n.language);
            }
            return null;
        }).filter(Boolean).join(', ') || '-';
    };

    const isCsvEligible = (r: Reservation) => {
        // Must be paid and not canceled
        // Also usually we export "shipping" pending items, but we allow completed too if re-issuing.
        return (r.payment_status === 'paid' || r.payment_status === 'completed') &&
            !['canceled', 'cancelled', 'user_cancelled', 'admin_cancelled', 'doctor_cancelled', 'expired'].includes(r.status);
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            // Select ALL eligible in current filtered view
            const newSet = new Set<number>();
            filteredReservations.forEach(r => {
                // We allow selecting anything, or just eligible? Usually anything for batch actions, but CSV filters inside.
                // Let's allow selecting all visible for flexibility.
                newSet.add(r.id);
            });
            setSelectedIds(newSet);
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSelectRow = (id: number) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedIds(newSet);
    };

    const openCsvModal = () => {
        // Calculate default ship date (tomorrow)
        const d = new Date();
        d.setDate(d.getDate() + 1);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        setCsvShipDate(`${yyyy}-${mm}-${dd}`);

        setShowCsvModal(true);
    };

    const handleGenerateCsv = async () => {
        // Collect eligible IDs from selection
        // If selection is empty, maybe warn? Or export all visible eligible?
        // AdminReservations logic: "selected" takes precedence. If none selected, disabled.

        const targetIds = Array.from(selectedIds).filter(id => {
            const r = reservations.find(res => res.id === id);
            return r && isCsvEligible(r);
        });

        if (targetIds.length === 0) {
            alert(t('admin.csv_no_eligible') || 'CSV出力できる対象（支払済み）が選択されていません。');
            return;
        }

        try {
            const response = await api.post(
                '/admin/reservations/csv/yamato',
                {
                    reservation_ids: targetIds,
                    cool_type: csvCoolType,
                    ship_date: csvShipDate,
                },
                {
                    responseType: 'blob', // Binary
                }
            );

            // Trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            link.setAttribute('download', `yamato_refill_export_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setShowCsvModal(false);
        } catch (error) {
            console.error(error);
            alert('CSV Failed');
        }
    };

    const [activeTab, setActiveTab] = useState('shipping'); // Default to shipping (pending)

    const filteredReservations = React.useMemo(() => {
        let filtered = reservations;

        if (activeTab === 'shipping') {
            filtered = filtered.filter(r =>
                (r.payment_status === 'paid' || r.payment_status === 'completed') &&
                r.shipping_status !== 'shipped' &&
                !['canceled', 'cancelled', 'user_cancelled', 'admin_cancelled', 'doctor_cancelled', 'expired'].includes(r.status)
            );
        } else if (activeTab === 'history') {
            filtered = filtered.filter(r =>
                r.shipping_status === 'shipped' &&
                !['canceled', 'cancelled', 'user_cancelled', 'admin_cancelled', 'doctor_cancelled', 'expired'].includes(r.status)
            );
        } else if (activeTab === 'canceled') {
            filtered = filtered.filter(r =>
                ['canceled', 'cancelled', 'user_cancelled', 'admin_cancelled', 'doctor_cancelled', 'expired'].includes(r.status)
            );
        }

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            filtered = filtered.filter(r =>
                (r.id.toString().includes(lower)) ||
                (r.user_name || '').toLowerCase().includes(lower) ||
                (r.user_email || '').toLowerCase().includes(lower)
            );
        }
        return filtered;
    }, [reservations, activeTab, searchTerm]);

    // Clear selection when tab changes to avoid confusion
    useEffect(() => {
        setSelectedIds(new Set());
    }, [activeTab]);

    const handleBatchApply = async () => {
        if (!window.confirm(t('admin.confirm_batch_apply', '表示中のすべての予約に一括適用しますか？'))) {
            return;
        }

        try {
            setLoading(true);
            const promises = filteredReservations.map(async (r) => {
                // Determine limits based on plan name (smart logic)
                const planDisplay = getMedicineDisplay(r) || '';

                // Check if plan implies monthly limit
                // Keywords: "1 month", "1か月", "1ヶ月"
                const isMonthlyPlan = /1\s*(month|か月|ヶ月)/i.test(planDisplay);

                const payload: any = {};
                if (isMonthlyPlan) {
                    // Apply to monthly limit if it's a monthly plan
                    if (batchValues.months > 0) {
                        payload.refill_max_months = batchValues.months;
                    }
                } else {
                    // Apply to quantity limit otherwise
                    if (batchValues.quantity > 0) {
                        payload.refill_max_quantity = batchValues.quantity;
                    }
                }

                // If no valid input for the detected type, skip
                if (Object.keys(payload).length === 0) return;

                // Call individual update endpoint
                await api.put(`/admin/reservations/${r.id}/refill-limits`, payload);
            });

            await Promise.all(promises);
            setShowBatchModal(false);
            await fetchInitialData();
            alert(t('common.saved'));
        } catch (e) {
            console.error(e);
            alert(t('common.save_failed'));
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (r: Reservation) => {
        setEditTarget(r);

        // Populate editLimits from r.selected_price_plan_ids
        const initialLimits: Record<number, { max_quantity: number; max_months: number }> = {};
        const plans = r.selected_price_plan_ids || [];
        const existing = r.refill_limits || {};

        plans.forEach(pid => {
            // Check if it's a valid medicine plan (not shipping)
            const info = planMap.get(pid);
            if (!info || info.serviceType === 'shipping') return;

            const current = existing[pid] || {};
            initialLimits[pid] = {
                max_quantity: current.max_quantity || 0,
                max_months: current.max_months || 0
            };
        });

        setEditLimits(initialLimits);
        setShowEditModal(true);
    };

    const openTrackingModal = (r: Reservation) => {
        setTrackingTarget(r);
        setTrackingNumber(r.tracking_number || '');
        setTrackingCarrier(r.shipping_carrier === 'yu_pack' ? 'yupack' : (r.shipping_carrier || 'yamato'));
        setShowTrackingModal(true);
    };

    const handleSaveTracking = async () => {
        if (!trackingTarget) return;
        try {
            await api.patch(`/admin/reservations/${trackingTarget.id}/tracking`, {
                tracking_number: trackingNumber,
                shipping_carrier: trackingCarrier
            });
            setShowTrackingModal(false);
            await fetchInitialData();
            alert(t('common.saved'));
        } catch (e) {
            console.error(e);
            alert(t('common.save_failed'));
        }
    };

    const handleSaveIndividual = async () => {
        if (!editTarget) return;
        try {
            // Convert limits to the format expected by backend (string keys)
            const payloadLimits: Record<string, any> = {};
            Object.entries(editLimits).forEach(([pid, val]) => {
                payloadLimits[pid] = val;
            });

            await api.put(`/admin/reservations/${editTarget.id}/refill-limits`, {
                refill_limits: payloadLimits
            });
            setShowEditModal(false);
            await fetchInitialData();
            alert(t('common.saved'));
        } catch (e) {
            console.error(e);
            alert(t('common.save_failed'));
        }
    };

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <FaSyncAlt className="text-amber-500" />
                        {t('admin.menu.refill') || '追加配送管理'}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {t('admin.refill_management_desc') || '患者の追加配送（リフィル）上限と使用状況を管理します'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={openCsvModal}
                        disabled={selectedIds.size === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition shadow-sm"
                        title={t('admin.csv_only_completed_paid') || '完了&支払済のみCSV出力できます'}
                    >
                        <FaFileCsv />
                        {t('admin.generate_csv') || 'CSV出力 (ヤマト)'}
                        {selectedIds.size > 0 && ` (${selectedIds.size})`}
                    </button>
                </div>
            </div>
            {/* Header / Tabs */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                    <button
                        onClick={() => setActiveTab('shipping')}
                        className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'shipping' ? 'bg-white text-coral shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('admin.tab_shipping', '未発送')}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'history' ? 'bg-white text-coral shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('admin.tab_history', '完了/履歴')}
                    </button>
                    <button
                        onClick={() => setActiveTab('canceled')}
                        className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'canceled' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('admin.tab_canceled', 'キャンセル')}
                    </button>
                </div>

                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder={t('admin.search_placeholder', '名前、メール、IDで検索')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-coral w-full md:w-64"
                    />
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <h2 className="font-bold text-gray-700 flex items-center gap-2">
                        {activeTab === 'shipping' && <span className="w-2 h-2 rounded-full bg-orange-500"></span>}
                        {activeTab === 'history' && <span className="w-2 h-2 rounded-full bg-gray-500"></span>}
                        {activeTab === 'canceled' && <span className="w-2 h-2 rounded-full bg-red-500"></span>}

                        {activeTab === 'shipping' && t('admin.shipping_pending_title', '未発送一覧')}
                        {activeTab === 'history' && t('admin.refill_history_title', '追加配送履歴')}
                        {activeTab === 'canceled' && t('admin.canceled_list_title', 'キャンセル一覧')}
                        <span className="text-sm font-normal text-gray-400 ml-2">({filteredReservations.length})</span>
                    </h2>

                    {/* Batch Actions */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowBatchModal(true)}
                            className="bg-coral hover:opacity-90 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition flex items-center gap-2"
                        >
                            <FaSyncAlt /> {t('admin.batch_settings', '一括設定')}
                        </button>
                        <button
                            onClick={() => fetchInitialData()}
                            className="text-gray-500 hover:text-coral p-2 rounded-full hover:bg-coral/5 transition"
                            title={t('common.reload')}
                        >
                            <FaSyncAlt />
                        </button>
                    </div>

                </div>

                <div className="overflow-x-auto">
                    {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
                    <div className="md:hidden divide-y">
                        {loading ? (
                            // ローディング状態（デスクトップ表と同一）
                            <div className="px-4 py-8 text-center text-gray-500">{t('common.loading')}</div>
                        ) : filteredReservations.length === 0 ? (
                            // 空状態（デスクトップ表と同一）
                            <div className="px-4 py-8 text-center text-gray-500">{t('common.no_data')}</div>
                        ) : (
                            filteredReservations.map((r: Reservation) => (
                                <div key={r.id} className={`p-4 ${selectedIds.has(r.id) ? 'bg-coral/5' : ''}`}>
                                    {/* 選択チェック + 患者情報 */}
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="mt-1 shrink-0"
                                            checked={selectedIds.has(r.id)}
                                            onChange={() => handleSelectRow(r.id)}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[10px] text-gray-400 font-mono">#{r.id}</div>
                                            <div className="font-bold text-gray-800 break-words">{r.user_name || '-'}</div>
                                            <div className="text-[11px] text-gray-400 break-all">{r.user_email}</div>
                                        </div>
                                    </div>

                                    {/* 处方药品 */}
                                    <div className="mt-3 text-sm min-w-0">
                                        <span className="text-gray-400">{t('admin.medicine_name', '处方药品')}：</span>
                                        <span className="text-gray-700 break-words whitespace-pre-wrap leading-snug">{getMedicineDisplay(r)}</span>
                                    </div>

                                    {/* 配送项目 */}
                                    <div className="mt-2 text-sm min-w-0">
                                        <span className="text-gray-400">{t('admin.shipping_fee', '配送项目')}：</span>
                                        <span className="text-gray-500 break-words whitespace-pre-wrap leading-snug">{getShippingDisplay(r)}</span>
                                    </div>

                                    {/* 残り数 / 合計 */}
                                    <div className="mt-2">
                                        <div className="text-sm text-gray-400 mb-1">{t('admin.refill_remaining_quantity', '残り数 / 合計')}：</div>
                                        <div className="flex flex-col gap-1">
                                            {r.refill_limits && Object.keys(r.refill_limits).length > 0 ? (
                                                // 医薬品ごとの使用状況表示
                                                Object.entries(r.refill_limits).map(([pid, limit]) => {
                                                    const info = planMap.get(Number(pid));
                                                    if (!info) return null;
                                                    const showQ = (limit.max_quantity || 0) > 0;
                                                    const showM = (limit.max_months || 0) > 0;
                                                    const remQ = limit.remaining_quantity !== undefined ? limit.remaining_quantity : ((limit.max_quantity || 0) - (limit.used_quantity || 0));
                                                    const remM = limit.remaining_months !== undefined ? limit.remaining_months : ((limit.max_months || 0) - (limit.used_months || 0));
                                                    return (
                                                        <div key={pid} className="text-xs bg-slate-50 px-2 py-1 rounded border border-slate-200">
                                                            <div className="font-bold text-slate-600 mb-0.5 break-words">{translateMedicineName(info.medName, i18n.language)}</div>
                                                            {showQ && (
                                                                <div className={remQ <= 0 ? 'text-red-600 font-bold' : ''}>
                                                                    Qty: {remQ} / {limit.max_quantity}
                                                                </div>
                                                            )}
                                                            {showM && (
                                                                <div className={remM <= 0 ? 'text-red-600 font-bold' : ''}>
                                                                    Mth: {remM} / {limit.max_months}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                // レガシー使用状況表示
                                                <div className="inline-flex items-center gap-2 bg-slate-100 px-2 py-1 rounded text-xs font-medium text-slate-700 w-fit">
                                                    {t('admin.refill_used_quantity_label', '使用数量')} ({r.refill_used_quantity} / {r.refill_max_quantity}) {t('admin.quantity_unit', '个')}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 配送状态 */}
                                    <div className="mt-2">
                                        <div className="text-sm text-gray-400 mb-1">{t('admin.shipping_status', '配送状态')}：</div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${r.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                {r.payment_status ? t(`status.${r.payment_status}`, r.payment_status) : '-'}
                                            </span>
                                            {r.shipping_status === 'shipped' ? (
                                                <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 shrink-0">
                                                    <FaCheckCircle size={10} /> {t('admin.shipped')}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-orange-600 font-bold flex items-center gap-1 shrink-0">
                                                    <FaExclamationCircle size={10} /> {t('admin.shipping_pending')}
                                                </span>
                                            )}
                                            {r.tracking_number && (
                                                <div className="w-full text-[10px] text-gray-500 font-mono break-all">
                                                    {r.tracking_number}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 操作（追跡番号入力・編集） */}
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        {r.payment_status === 'paid' && (
                                            <button
                                                onClick={() => openTrackingModal(r)}
                                                className={`shrink-0 px-3 py-1 text-xs rounded transition shadow-sm ${r.shipping_status === 'shipped'
                                                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                                                    : 'bg-coral text-white hover:opacity-90'}`}
                                            >
                                                {r.shipping_status === 'shipped' ? (t('admin.edit_tracking') || '追跡番号変更') : t('admin.shipping_input_tracking')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => openEditModal(r)}
                                            className="shrink-0 flex items-center gap-1 px-3 py-1 text-xs text-gray-500 hover:text-coral hover:bg-coral/5 rounded-lg transition border border-gray-200"
                                            title={t('admin.edit_refill_limit')}
                                        >
                                            <FaEdit /> {t('admin.edit_refill_limit')}
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <table className="w-full text-sm text-left hidden md:table">
                        <thead className="bg-gray-100 text-gray-700 uppercase text-xs font-bold">
                            <tr>
                                <th className="px-4 py-3 w-4">
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={filteredReservations.length > 0 && filteredReservations.every(r => selectedIds.has(r.id))}
                                    />
                                </th>
                                <th className="px-4 py-3">{t('doctor.patient')}</th>
                                <th className="px-4 py-3">{t('admin.medicine_name', '处方药品')}</th>
                                <th className="px-4 py-3">{t('admin.shipping_fee', '配送项目')}</th>
                                <th className="px-4 py-3">{t('admin.refill_remaining_quantity', '残り数 / 合計')}</th>
                                <th className="px-4 py-3">{t('admin.shipping_status', '配送状态')}</th>
                                <th className="px-4 py-3 text-right">{t('admin.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y text-gray-600">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center">{t('common.loading')}</td>
                                </tr>
                            ) : filteredReservations.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center">{t('common.no_data')}</td>
                                </tr>
                            ) : (
                                filteredReservations.map((r: Reservation) => (
                                    <tr key={r.id} className={`hover:bg-gray-50 transition ${selectedIds.has(r.id) ? 'bg-coral/5' : ''}`}>
                                        <td className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(r.id)}
                                                onChange={() => handleSelectRow(r.id)}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
                                                </div>
                                                <div>
                                                    <div className="text-[10px] text-gray-400 font-mono">#{r.id}</div>
                                                    <div className="font-bold text-gray-800">{r.user_name || '-'}</div>
                                                    <div className="text-[11px] text-gray-400">{r.user_email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="max-w-[200px] whitespace-pre-wrap leading-snug">
                                                {getMedicineDisplay(r)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="max-w-[150px] whitespace-pre-wrap leading-snug text-gray-500">
                                                {getShippingDisplay(r)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                {r.refill_limits && Object.keys(r.refill_limits).length > 0 ? (
                                                    // Per-medicine usage display
                                                    Object.entries(r.refill_limits).map(([pid, limit]) => {
                                                        const info = planMap.get(Number(pid));
                                                        if (!info) return null;
                                                        // Determine which stat to show (Quantity or Months)
                                                        const showQ = (limit.max_quantity || 0) > 0;
                                                        const showM = (limit.max_months || 0) > 0;

                                                        // Use calculated remaining if available
                                                        const remQ = limit.remaining_quantity !== undefined ? limit.remaining_quantity : ((limit.max_quantity || 0) - (limit.used_quantity || 0));
                                                        const remM = limit.remaining_months !== undefined ? limit.remaining_months : ((limit.max_months || 0) - (limit.used_months || 0));

                                                        return (
                                                            <div key={pid} className="text-xs bg-slate-50 px-2 py-1 rounded border border-slate-200">
                                                                <div className="font-bold text-slate-600 mb-0.5">{translateMedicineName(info.medName, i18n.language)}</div>
                                                                {showQ && (
                                                                    <div className={remQ <= 0 ? 'text-red-600 font-bold' : ''}>
                                                                        Qty: {remQ} / {limit.max_quantity}
                                                                    </div>
                                                                )}
                                                                {showM && (
                                                                    <div className={remM <= 0 ? 'text-red-600 font-bold' : ''}>
                                                                        Mth: {remM} / {limit.max_months}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    // Legacy usage display
                                                    <div className="inline-flex items-center gap-2 bg-slate-100 px-2 py-1 rounded text-xs font-medium text-slate-700">
                                                        {t('admin.refill_used_quantity_label', '使用数量')} ({r.refill_used_quantity} / {r.refill_max_quantity}) {t('admin.quantity_unit', '个')}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase w-fit ${r.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                    {r.payment_status ? t(`status.${r.payment_status}`, r.payment_status) : '-'}
                                                </span>
                                                {r.shipping_status === 'shipped' ? (
                                                    <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                                                        <FaCheckCircle size={10} /> {t('admin.shipped')}
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] text-orange-600 font-bold flex items-center gap-1">
                                                        <FaExclamationCircle size={10} /> {t('admin.shipping_pending')}
                                                    </span>
                                                )}
                                                {r.tracking_number && (
                                                    <div className="text-[10px] text-gray-500 mt-1 font-mono pl-1">
                                                        {r.tracking_number}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                {r.payment_status === 'paid' && (
                                                    <button
                                                        onClick={() => openTrackingModal(r)}
                                                        className={`px-3 py-1 text-xs rounded transition shadow-sm ${r.shipping_status === 'shipped'
                                                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                                                            : 'bg-coral text-white hover:opacity-90'}`}
                                                    >
                                                        {r.shipping_status === 'shipped' ? (t('admin.edit_tracking') || '追跡番号変更') : t('admin.shipping_input_tracking')}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => openEditModal(r)}
                                                    className="p-2 text-gray-400 hover:text-coral hover:bg-coral/5 rounded-lg transition"
                                                    title={t('admin.edit_refill_limit')}
                                                >
                                                    <FaEdit />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Modal */}
            {
                showEditModal && editTarget && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl animate-in fade-in zoom-in duration-200 cursor-default" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-2 text-xl font-bold mb-4 text-gray-800">
                                <FaEdit className="text-coral" />
                                {t('admin.refill_settings')}
                            </div>

                            <div className="bg-slate-50 p-3 rounded-xl mb-6">
                                <div className="text-xs text-slate-400 font-mono mb-1">ID: {editTarget?.id}</div>
                                <div className="font-bold text-gray-800">{editTarget?.user_name} (Email: {editTarget?.user_email})</div>
                            </div>

                            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                                {Object.keys(editLimits).length === 0 ? (
                                    <div className="text-center text-gray-500 py-4">
                                        {t('admin.no_medicines_found', '対象の医薬品が見つかりません')}
                                    </div>
                                ) : (
                                    Object.entries(editLimits).map(([pidStr, val]) => {
                                        const pid = parseInt(pidStr);
                                        const info = planMap.get(pid);
                                        if (!info) return null;

                                        // Get current usage
                                        const currentLimit = editTarget?.refill_limits?.[pidStr] || {};
                                        const usedQ = currentLimit.used_quantity || 0;
                                        const usedM = currentLimit.used_months || 0;

                                        return (
                                            <div key={pid} className="border rounded-xl p-4 bg-gray-50">
                                                <div className="font-bold text-gray-700 mb-3 border-b pb-2">
                                                    {translateMedicineName(info.medName, i18n.language)}
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 mb-1">
                                                            {t('admin.refill_limit_quantity')} ({t('admin.quantity_unit')})
                                                        </label>
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                className="w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                                                                value={val.max_quantity}
                                                                onChange={(e) => {
                                                                    const newVal = parseInt(e.target.value) || 0;
                                                                    setEditLimits(prev => ({
                                                                        ...prev,
                                                                        [pid]: { ...prev[pid], max_quantity: newVal }
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="text-xs text-right text-gray-400 mt-1">
                                                            Used: {usedQ}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 mb-1">
                                                            {t('admin.refill_limit_months')} ({t('admin.months_unit')})
                                                        </label>
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                className="w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                                                                value={val.max_months}
                                                                onChange={(e) => {
                                                                    const newVal = parseInt(e.target.value) || 0;
                                                                    setEditLimits(prev => ({
                                                                        ...prev,
                                                                        [pid]: { ...prev[pid], max_months: newVal }
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="text-xs text-right text-gray-400 mt-1">
                                                            Used: {usedM}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <div className="flex gap-3 mt-8">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="flex-1 px-4 py-3 border rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    onClick={handleSaveIndividual}
                                    className="flex-1 px-4 py-3 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap font-bold hover:opacity-90 transition"
                                >
                                    {t('common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Batch Modal */}
            {
                showBatchModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
                            <div className="flex items-center gap-2 text-xl font-bold mb-2 text-coral">
                                <FaSyncAlt />
                                {t('admin.batch_refill_limit')}
                            </div>
                            <p className="text-xs text-gray-500 mb-6 font-medium bg-coral/5 p-2 rounded leading-relaxed">
                                {t('admin.smart_batch_desc', '※プラン名（例：「1 month」「1か月」）を自動判別し、1か月プランには「月数設定」、それ以外には「数量設定」を適用します。')}
                            </p>

                            <div className="space-y-4">
                                <div className="p-3 border rounded-xl bg-gray-50">
                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                        {t('admin.if_monthly_plan', '①「1か月」プランの場合')}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-gray-700">{t('admin.refill_limit_months')} :</span>
                                        <input
                                            type="number"
                                            min="0"
                                            className="w-20 border rounded px-2 py-1 outline-none focus:ring-2 focus:ring-blue-300"
                                            value={batchValues.months}
                                            onChange={(e) => setBatchValues({ ...batchValues, months: parseInt(e.target.value) || 0 })}
                                        />
                                        <span className="text-sm text-gray-600">{t('admin.months_unit')}</span>
                                    </div>
                                </div>

                                <div className="p-3 border rounded-xl bg-gray-50">
                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                        {t('admin.if_other_plan', '② その他のプランの場合')}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-gray-700">{t('admin.refill_limit_quantity')} :</span>
                                        <input
                                            type="number"
                                            min="0"
                                            className="w-20 border rounded px-2 py-1 outline-none focus:ring-2 focus:ring-blue-300"
                                            value={batchValues.quantity}
                                            onChange={(e) => setBatchValues({ ...batchValues, quantity: parseInt(e.target.value) || 0 })}
                                        />
                                        <span className="text-sm text-gray-600">{t('admin.quantity_unit')}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-8">
                                <button
                                    onClick={() => setShowBatchModal(false)}
                                    className="flex-1 px-4 py-3 border rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    onClick={handleBatchApply}
                                    className="flex-1 px-4 py-3 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap font-bold hover:opacity-90 transition"
                                >
                                    {t('common.apply')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Tracking Modal */}
            {
                showTrackingModal && trackingTarget && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
                            <h3 className="text-xl font-bold mb-4">{t('admin.tracking_input_title')}</h3>
                            <input
                                type="text"
                                className="w-full border rounded-xl px-4 py-2 mb-4 outline-none focus:ring-2 focus:ring-blue-300"
                                placeholder={t('admin.tracking_input_placeholder')}
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                            />
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">{t('admin.shipping_carrier') || '配送業者'}</label>
                            <select
                                value={trackingCarrier}
                                onChange={(e) => setTrackingCarrier(e.target.value)}
                                className="w-full border rounded-xl px-4 py-2 mb-6 outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                            >
                                <option value="yamato">{t('admin.carrier_yamato') || 'ヤマト運輸'}</option>
                                <option value="sagawa">{t('admin.carrier_sagawa') || '佐川急便'}</option>
                                <option value="yupack">{t('admin.carrier_yu_pack') || '日本郵便'}</option>
                            </select>
                            <div className="flex gap-3">
                                <button onClick={() => setShowTrackingModal(false)} className="flex-1 px-4 py-2 border rounded-xl font-bold">
                                    {t('common.cancel')}
                                </button>
                                <button onClick={handleSaveTracking} className="flex-1 px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap font-bold hover:opacity-90 transition">
                                    {t('common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* CSV Modal */}
            {
                showCsvModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                {t('admin.csv_export_title') || 'ヤマトB2CSV出力'}
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">{t('admin.csv_cool_type') || '配送タイプ'}</label>
                                    <div className="flex flex-col gap-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="coolType" value={0} checked={csvCoolType === 0} onChange={() => setCsvCoolType(0)} />
                                            <span>{t('admin.csv_normal') || '普通'}</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="coolType" value={1} checked={csvCoolType === 1} onChange={() => setCsvCoolType(1)} />
                                            <span>{t('admin.csv_cool') || '冷蔵'}</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="coolType" value={2} checked={csvCoolType === 2} onChange={() => setCsvCoolType(2)} />
                                            <span>{t('admin.csv_frozen') || '冷凍'}</span>
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">{t('admin.csv_ship_date') || '出荷予定日'}</label>
                                    <input
                                        type="date"
                                        className="w-full border rounded px-3 py-2"
                                        value={csvShipDate}
                                        onChange={(e) => setCsvShipDate(e.target.value)}
                                    />
                                </div>
                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        onClick={() => setShowCsvModal(false)}
                                        className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                                    >
                                        {t('common.cancel')}
                                    </button>
                                    <button
                                        onClick={handleGenerateCsv}
                                        className="px-4 py-2 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 shadow-sm"
                                    >
                                        {t('common.export') || '出力'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default AdminAdditionalSupply;
